import { useCallback, useEffect } from 'react';

import type { GenericDetails } from 'components/Input/Autocomplete';

import type { Field } from '../types';
import { generateValues } from '../utils';
import { track } from '@amplitude/analytics-browser';
import { useAppContext } from 'store/AppProvider';

type ValuesStore = Record<string, GenericDetails>;

const useToolbarFields = (screenName: string, fields: Field[][]) => {
    const { appState, generateToolbarFields, setToolbarValue } = useAppContext();

    const formData = appState.toolbar[screenName];

    const generateValuesStore = useCallback(() => {
        const values = fields.reduce<ValuesStore>((acc, layer) => {
            layer.forEach((field) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                acc[field.name] = field.defaultValue ?? generateValues();
            });
            return acc;
        }, {});

        generateToolbarFields({ name: screenName, value: values });
    }, [fields, screenName, generateToolbarFields]);

    const generateEmptyValues = useCallback(() => {
        const values = fields.reduce<ValuesStore>((acc, layer) => {
            layer.forEach((field) => {
                acc[field.name] = generateValues();
            });
            return acc;
        }, {});

        generateToolbarFields({ name: screenName, value: values });
    }, [fields]);

    const setFieldValue = (fieldName: string, fieldValue: GenericDetails) => {
        track({ event_type: `Filter ${screenName} By ${fieldName} Clicked`, event_properties: fieldValue });
        setToolbarValue({ name: screenName, fieldName, fieldValue });
    };

    const handleResetFilter = () => {
        generateEmptyValues();
    };

    useEffect(() => {
        generateValuesStore();
    }, []);

    return {
        setFieldValue,
        handleResetFilter,
        formData: formData ?? {},
    };
};

export { useToolbarFields };
